import { useState } from 'react';
import { useGetAllInspectionRecords, getDefectTypeLabel, getSeverityLabel, getSeverityBadgeClass } from '../hooks/useQueries';
import { generateInspectionPDF } from '../utils/pdfGenerator';
import { InspectionRecord } from '../services/localBackend';
import { FileText, Download, Search, Loader2, Eye } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

export default function Reports() {
  const { data: records = [], isLoading } = useGetAllInspectionRecords();
  const [searchQuery, setSearchQuery] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<InspectionRecord | null>(null);

  const filteredRecords = records.filter(r =>
    r.componentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getDefectTypeLabel(r.defectType).toLowerCase().includes(searchQuery.toLowerCase()) ||
    getSeverityLabel(r.finalClassification).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDownloadPDF = async (record: InspectionRecord) => {
    const key = record.componentId + String(record.timestamp);
    setGeneratingPDF(key);
    try {
      await generateInspectionPDF(record);
    } finally {
      setGeneratingPDF(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">Inspection Reports</h2>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
            Generate and download PDF reports for inspection records
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileText className="w-4 h-4" />
          <span>{records.length} total records</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by component ID, defect type, or classification..."
          className="w-full bg-input border border-border rounded pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-electric-blue focus:border-electric-blue transition-colors"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Records list */}
        <div className="xl:col-span-2 bg-card border border-border rounded-lg shadow-card-dark overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Inspection Records</h3>
            <p className="text-xs text-muted-foreground">
              {filteredRecords.length} of {records.length} records
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {['Component ID', 'Timestamp', 'Defect Type', 'Classification', 'Severity', 'Actions'].map(h => (
                    <th key={h} className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b border-border/50">
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="py-3 px-4">
                          <Skeleton className="h-4 w-full bg-muted/50" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-muted-foreground text-sm">
                      {records.length === 0 ? 'No inspection records found. Run an inspection first.' : 'No records match your search.'}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record, idx) => {
                    const key = record.componentId + String(record.timestamp);
                    const isGenerating = generatingPDF === key;
                    const isSelected = selectedRecord?.componentId === record.componentId &&
                      selectedRecord?.timestamp === record.timestamp;

                    return (
                      <tr
                        key={idx}
                        className={`border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer ${isSelected ? 'bg-electric-blue/5 border-l-2 border-l-electric-blue' : ''}`}
                        onClick={() => setSelectedRecord(isSelected ? null : record)}
                      >
                        <td className="py-3 px-4 font-mono text-xs text-electric-blue whitespace-nowrap">
                          {record.componentId}
                        </td>
                        <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">
                          {formatTimestamp(record.timestamp)}
                        </td>
                        <td className="py-3 px-4 text-xs text-foreground">
                          {getDefectTypeLabel(record.defectType)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getSeverityBadgeClass(record.finalClassification)}`}>
                            {getSeverityLabel(record.finalClassification)}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs text-muted-foreground">
                          {String(record.severityScore)}/100
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => setSelectedRecord(isSelected ? null : record)}
                              className="p-1.5 rounded text-muted-foreground hover:text-electric-blue hover:bg-electric-blue/10 transition-colors"
                              title="Preview"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(record)}
                              disabled={isGenerating}
                              className="p-1.5 rounded text-muted-foreground hover:text-electric-blue hover:bg-electric-blue/10 transition-colors disabled:opacity-50"
                              title="Download PDF"
                            >
                              {isGenerating ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Download className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Record detail / PDF preview */}
        <div className="bg-card border border-border rounded-lg shadow-card-dark">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Report Preview</h3>
          </div>

          {!selectedRecord ? (
            <div className="p-8 text-center">
              <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Select a record to preview report details</p>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div className={`p-3 rounded border ${getSeverityBadgeClass(selectedRecord.finalClassification)}`}>
                <p className="text-xs uppercase tracking-wider mb-0.5 opacity-70">Classification</p>
                <p className="text-lg font-bold">{getSeverityLabel(selectedRecord.finalClassification)}</p>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Component ID', value: selectedRecord.componentId, mono: true },
                  { label: 'Timestamp', value: formatTimestamp(selectedRecord.timestamp) },
                  { label: 'Defect Type', value: getDefectTypeLabel(selectedRecord.defectType) },
                  { label: 'Severity Score', value: `${selectedRecord.severityScore}/100` },
                  { label: 'Model Confidence', value: `${selectedRecord.modelConfidence}%` },
                ].map(({ label, value, mono }) => (
                  <div key={label} className="flex items-start justify-between gap-2">
                    <span className="text-xs text-muted-foreground flex-shrink-0">{label}</span>
                    <span className={`text-xs font-semibold text-foreground text-right ${mono ? 'font-mono text-electric-blue' : ''}`}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleDownloadPDF(selectedRecord)}
                disabled={generatingPDF !== null}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded bg-electric-blue text-background font-bold text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50 glow-blue"
              >
                {generatingPDF ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                ) : (
                  <><Download className="w-4 h-4" /> Download PDF Report</>
                )}
              </button>

              <p className="text-[10px] text-muted-foreground text-center">
                PDF will open in a new window for printing/saving
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
